import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProposalProductItemType } from '@econtractor/types';
import { useToast } from 'context/ToastContext';
import {
    hasSyncableProductPrices,
    syncProductRowsPrices,
} from '../../model/syncCatalogPrices';

export interface UseUpdateProductPricesParams {
    products: ProposalProductItemType[];
    rate: number;
    localeNs?: string;
    onChange: (products: ProposalProductItemType[]) => void;
}

const useUpdateProductPrices = ({
    products,
    rate,
    localeNs = 'Proposal',
    onChange,
}: UseUpdateProductPricesParams) => {
    const { t } = useTranslation([localeNs, 'common']);
    const { showToast } = useToast();
    const [isUpdating, setIsUpdating] = useState(false);

    const canUpdate = hasSyncableProductPrices(products);

    const handleUpdatePrices = async () => {
        if (!canUpdate || isUpdating) return;
        setIsUpdating(true);
        try {
            const next = await syncProductRowsPrices(products, rate);
            onChange(next);
            showToast(t('edit.products.updatePricesSuccess'), 'success');
        } catch (e) {
            console.error(e);
            showToast(t('common:messError'), 'danger');
        } finally {
            setIsUpdating(false);
        }
    };

    return {
        isUpdating,
        canUpdate,
        handleUpdatePrices,
    };
};

export default useUpdateProductPrices;
